// @ts-nocheck
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import reorder from "@insite/client-framework/Store/Pages/OrderStatus/Handlers/Reorder";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
// import { ButtonPresentationProps } from "@insite/mobius/Button"; // Store Custom Code
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
/* import OrderActionSet, {
    orderActionSetFieldDefinitions,
    OrderActionSetPresentationProps,
} from "@store/Library/Components/Toolkit/Order/OrderActionSet"; */ // Store Custom Code
// import { LinkPresentationProps } from "@store/MobiusOverrides/Link/Link"; // Store Custom Code
// import ShareEntityButton, { ShareEntityButtonStyles } from "@store/Overrides/Components/ShareEntityButton"; // Store Custom Code
import ga from "@toolkit/Framework/Common/GoogleAnalytics";
import useExpandProps, { ExpandStyles } from "@toolkit/Framework/Common/Hooks/useExpandProps";
import getQueryString from "@toolkit/Framework/Common/Utilities/getQueryString";
import React from "react";
import { connect, ResolveThunks } from "react-redux";

/* Store Custom Code - Start */
import OrderActionSet, {
    orderActionSetFieldDefinitions,
    OrderActionSetPresentationProps,
} from "@store/Library/Components/Toolkit/Order/OrderActionSet";
import { ButtonPresentationProps } from "@store/MobiusOverrides/Button/Props";
import { LinkPresentationProps } from "@store/MobiusOverrides/Link/Link";
import ShareEntityButton, { ShareEntityButtonStyles } from "@store/Overrides/Components/ShareEntityButton";
/* Store Custom Code - End */

const enum fields {
    showAsButtons = "showAsButtons",
    showReorder = "showReorder",
    showPrint = "showPrint",
    showShareViaEmail = "showShareViaEmail",
}

interface OrderStatusActionsPresentationProps extends ExpandStyles<OrderStatusActionsStyles> {}

interface OrderStatusActionsProps extends WidgetProps, OrderStatusActionsPresentationProps {
    fields: {
        [fields.showAsButtons]: boolean;
        [fields.showReorder]: boolean;
        [fields.showPrint]: boolean;
        [fields.showShareViaEmail]: boolean;
    };
}

const mapStateToProps = (state: ApplicationState) => {
    const location = getLocation(state);
    const { stEmail, stemail, stPostalCode, stpostalcode } = getQueryString(location.search);

    return {
        order: state.pages.orderStatus.order,
        isReordering: state.pages.orderStatus.isReordering,
        isUpdatingCart: state.context.isUpdatingCart,
        showAddToCartConfirmationDialog: getSettingsCollection(state).productSettings.showAddToCartConfirmationDialog,
        stEmail: stEmail ?? stemail ?? "",
        stPostalCode: stPostalCode ?? stpostalcode ?? "",
    };
};

const mapDispatchToProps = {
    reorder,
};

type Props = OrderStatusActionsProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasToasterContext;

export interface OrderStatusActionsStyles {
    actionSet?: OrderActionSetPresentationProps;
    printButton?: ButtonPresentationProps;
    printLink?: LinkPresentationProps;
    reorderButton?: ButtonPresentationProps;
    reorderLink?: LinkPresentationProps;
    shareEntityStyles?: ShareEntityButtonStyles;
}

export const orderStatusActionsStyles: OrderStatusActionsStyles = {};

const OrderStatusActions = ({
    order,
    isReordering,
    isUpdatingCart,
    showAddToCartConfirmationDialog,
    stEmail,
    stPostalCode,
    toaster,
    reorder,
    fields: widgetFields,
    extendedStyles,
    overridedStyles,
}: Props) => {
    const styles = useExpandProps(orderStatusActionsStyles, extendedStyles, overridedStyles);
    const trackAddToCart = ga.use.trackAddToCart();

    if (!order) {
        return null;
    }

    const onClickReorder = () => {
        reorder({
            onSuccess: () => {
                trackAddToCart();
                if (!showAddToCartConfirmationDialog) {
                    return;
                }
                toaster.addToast({
                    body: `${order.webOrderNumber || order.erpOrderNumber} ${translate("Added to Cart")}`,
                    messageType: "success",
                    timeoutLength: 6000,
                });
            },
        });
    };

    return (
        <OrderActionSet
            {...styles.actionSet}
            fields={widgetFields}
            actions={[
                {
                    key: "reorder",
                    label: translate("Reorder"),
                    isDisabled: isUpdatingCart,
                    isLoading: isReordering,
                    isVisible: order.orderLines?.some(x => x.isActiveProduct),
                    onClick: onClickReorder,
                    buttonStyles: styles.reorderButton,
                    linkStyles: styles.reorderLink,
                    field: fields.showReorder,
                },
                {
                    preset: "print",
                    buttonStyles: styles.printButton,
                    linkStyles: styles.printLink,
                    field: fields.showPrint,
                },
                {
                    button: (
                        <ShareEntityButton
                            key="share"
                            entityId={order.webOrderNumber}
                            entityName="Order"
                            extendedStyles={styles.shareEntityStyles}
                            extraProperties={{ stEmail, stPostalCode }}
                        />
                    ),
                    link: (
                        <ShareEntityButton
                            key="share"
                            entityId={order.webOrderNumber}
                            entityName="Order"
                            extendedStyles={styles.shareEntityStyles}
                            extraProperties={{ stEmail, stPostalCode }}
                            variant="clickable"
                        />
                    ),
                    field: fields.showShareViaEmail,
                },
            ]}
        />
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withToaster(OrderStatusActions)),
    definition: {
        group: "Order Status",
        allowedContexts: ["OrderStatusPage"],
        displayName: "Actions",
        fieldDefinitions: orderActionSetFieldDefinitions([
            { name: fields.showReorder, defaultState: "sticky" },
            { name: fields.showShareViaEmail },
            { name: fields.showPrint },
        ]),
    },
};

export default widgetModule;
